import React, {
	type SetStateAction,
	type Dispatch,
	type MouseEvent,
	type PropsWithChildren,
} from 'react';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Flex } from '@atlaskit/primitives/compiled/flex';

import type { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck.ts';
import { usePageContentId } from '@confluence/page-context';

import { ViewReplyOptions } from '../helpers/repliesHelpers.ts';

import { RepliesFooterEditor } from './RepliesFooterEditor.tsx';
import { RepliesFooterActions } from './RepliesFooterActions.tsx';

const styles = cssMap({
	repliesFooter: {
		justifyContent: 'flex-end',
		paddingLeft: token('space.200'),
	},
	repliesFooterSpaceBetween: {
		justifyContent: 'space-between',
		paddingLeft: token('space.300'),
	},
});

type RepliesFooterProps = {
	replyView: ViewReplyOptions;
	setReplyView: Dispatch<SetStateAction<ViewReplyOptions>>;
	isEditorExpanded: boolean;
	numOfVisibleReplies: number;
	isParentCommentOpen: boolean;
	isStandaloneComment?: boolean;
	handleReplyButtonClick: (event: MouseEvent<HTMLButtonElement>) => void;
	isOffline?: boolean;
	scrollToId: string;
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	location: LocationValue;
};

export const RepliesFooter = ({
	replyView,
	setReplyView,
	isEditorExpanded,
	numOfVisibleReplies,
	isParentCommentOpen,
	children: replyEditor,
	handleReplyButtonClick,
	scrollToId,
	location,
	isStandaloneComment = false,
	isOffline = false,
}: PropsWithChildren<RepliesFooterProps>): React.JSX.Element => {
	const [contentId = ''] = usePageContentId();
	const { canAddComments } = useAddCommentPermissionCheck(contentId, true);

	const isReplyButtonVisible =
		!isEditorExpanded && canAddComments && isParentCommentOpen && !isStandaloneComment;

	const isHideRepliesToggleVisible =
		!isStandaloneComment && replyView !== ViewReplyOptions.HIDDEN && numOfVisibleReplies > 0;

	return (
		<Flex
			testId="replies-footer"
			xcss={isReplyButtonVisible ? styles.repliesFooterSpaceBetween : styles.repliesFooter}
			gap={isEditorExpanded ? 'space.100' : 'space.0'}
			direction={isEditorExpanded ? 'column' : 'row'}
			alignItems={isEditorExpanded ? 'start' : 'center'}
		>
			{isEditorExpanded ? (
				<RepliesFooterEditor>{replyEditor}</RepliesFooterEditor>
			) : (
				<RepliesFooterActions
					isHideRepliesToggleVisible={isHideRepliesToggleVisible}
					isReplyButtonVisible={isReplyButtonVisible}
					numOfVisibleReplies={numOfVisibleReplies}
					location={location}
					scrollToId={scrollToId}
					setReplyView={setReplyView}
					isOffline={isOffline}
					handleReplyButtonClick={handleReplyButtonClick}
				/>
			)}
		</Flex>
	);
};
